import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Phone, Mail, Clock, Target, Users, Trophy, Building2 } from 'lucide-react';

const About = () => {
  useEffect(() => {
    document.title = 'About Us - Pin High Golf & Lounge | Bay Village, Ohio';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about Pin High Golf & Lounge in Bay Village, Ohio—indoor golf simulators, PGA instruction, leagues, and a premium lounge in the former Bay Lanes space.');
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/about');
    updateSocialMeta({
      title: 'About Us - Pin High Golf & Lounge | Bay Village, Ohio',
      description: 'Learn about Pin High Golf & Lounge in Bay Village, Ohio—indoor golf simulators, PGA instruction, leagues, and a premium lounge in the former Bay Lanes space.',
      path: '/about',
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/about-hero.jpg"
            alt="Pin High Golf & Lounge interior"
            className="w-full h-full object-cover opacity-90"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <Building2 className="w-20 h-20 mx-auto mb-6 text-green-400" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            About Pin High
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-200">
            Bay Village's premier indoor golf destination
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                Our Story
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Built in the former Bay Lanes space, Pin High carries forward the spirit of community and fun that made this location a Bay Village favorite for generations—now reimagined as a modern golf destination on the Westside of Cleveland in Bay Village.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We've transformed this beloved space into a state-of-the-art indoor golf facility, combining cutting-edge Foresight simulator technology with a welcoming lounge atmosphere. Whether you're here to improve your game, compete in a league starting November 9th, or simply enjoy time with friends, Pin High is your year-round golf destination.
              </p>
              <p className="text-lg text-gray-600">
                Our mission is simple: make great golf accessible to everyone, regardless of weather, skill level, or schedule. From casual players to serious competitors, from families to corporate groups, we're here to help you play, learn, and connect.
              </p>
            </div>
            <div>
              <img
                src="/about-single.jpg"
                alt="Golf simulator technology at Pin High"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover opacity-95"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              What We Offer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for the perfect golf experience, all under one roof.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Foresight Simulators</h3>
                <p className="text-gray-600">
                  Play world-class courses with tour-level accuracy on our advanced Foresight simulator technology.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">PGA Instruction</h3>
                <p className="text-gray-600">
                  Learn from certified professionals who use data-driven instruction to help you improve your game.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Leagues & Private Events</h3>
                <p className="text-gray-600">
                  Join competitive leagues starting November 9th, or host private events in our dedicated party space.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Lounge & Dining</h3>
                <p className="text-gray-600">
                  Enjoy pizza from Wolf & Co., drinks, and a comfortable atmosphere perfect for relaxing after your round.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Visit Us</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Address</p>
                      <p className="text-gray-600">27229 Wolf Road<br />Bay Village, OH 44140</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Phone className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">Phone</p>
                      <a href="tel:4404551078" className="text-gray-600 hover:text-green-600 transition-colors">
                        (440) 455-1078
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Mail className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <a href="mailto:dennis@pinhighbay.com" className="text-gray-600 hover:text-green-600 transition-colors">
                        dennis@pinhighbay.com
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Hours</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Monday - Thursday</p>
                      <p className="text-gray-600">9:00 AM - 10:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 ml-10">
                    <div>
                      <p className="font-semibold text-gray-900">Friday</p>
                      <p className="text-gray-600">9:00 AM - 11:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 ml-10">
                    <div>
                      <p className="font-semibold text-gray-900">Saturday</p>
                      <p className="text-gray-600">10:00 AM - 11:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 ml-10">
                    <div>
                      <p className="font-semibold text-gray-900">Sunday</p>
                      <p className="text-gray-600">10:00 AM - 9:00 PM</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Experience Pin High?
          </h2>
          <p className="text-xl mb-8 text-green-50">
            Book your bay, join a league starting November 9th, schedule a lesson, or plan your next event. We're here to help you play your best game.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300"
            >
              <a href="https://app.whoosh.io/patron/club/pin-high-golf-lounge/agenda/pin-high/today" target="_blank" rel="noopener noreferrer">
                Book Your Bay
              </a>
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 text-lg px-8 py-6 transition-all duration-300"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
